import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const internalTransactionsCreate = createAsyncThunk(
  "internalTransactions/internalTransactionsCreate",
  async (values, { rejectWithValue }) => {
    const { internalCreateTxnData } = values;

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/transactions/internal`,
        internalCreateTxnData,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response || error?.message);
    }
  },
);

export const internalTransactionsRecreate = createAsyncThunk(
  "internalTransactions/internalTransactionsRecreate",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/refactor`,
        {
          txn_id: Array.isArray(id) ? id : [id],
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const internalTransactionsViewAll = createAsyncThunk(
  "internalTransactions/internalTransactionsViewAll",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_item_count,
      amount,
      min_amount,
      max_amount,
      lifecycle_status,
      payment_method,
      internal_account_id,
      owner_account_id,
      start_date,
      end_date,
      txn_id,
      entity_id,
      category,
      bank_identifier,
      distribution_lifecycle_status,
      additional_data,
      confidential_transaction,
      category_value,
      category_name,
      refactor,
      labels,
      created_via,
    } = values;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/internal`,
        {
          headers: getHeaders(),
          params: {
            page_no,
            page_size: page_item_count,
            amount,
            min_amount,
            max_amount,
            lifecycle_status,
            payment_method,
            internal_account_id,
            owner_account_id,
            start_date,
            end_date,
            txn_id,
            entity_id,
            category,
            bank_identifier,
            distribution_lifecycle_status,
            additional_data,
            confidential_transaction:
              confidential_transaction === "Show only confidential"
                ? "Y"
                : confidential_transaction === "Hide confidential"
                  ? "N"
                  : undefined,
            category_name,
            category_value,
            refactor,
            labels,
            created_via,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        message:
          error?.response?.data?.errors?.base?.[0] ||
          error?.response?.data?.base?.[0] ||
          error?.response?.data?.message ||
          error?.message,
        errors: error?.response?.data?.errors || {},
        data: error?.response?.data,
      });
    }
  },
);

export const internalTransactionsViewAllWithBody = createAsyncThunk(
  /**
   * This endpoint retrieves Internal Transactions. It is specifically provided to support requests with
   * long query parameters that may exceed the URL length limitations of the GET endpoint.
   * The API uses the PUT method to allow filtering and other request parameters to be passed in the request body,
   * thereby avoiding query parameter length limitations.
   *
   */

  "internalTransactions/internalTransactionsViewAllWithBody",
  async (values, { rejectWithValue }) => {
    const {
      page_no,
      page_item_count,
      amount,
      min_amount,
      max_amount,
      lifecycle_status,
      payment_method,
      internal_account_id,
      owner_account_id,
      start_date,
      end_date,
      txn_id,
      entity_id,
      category,
      bank_identifier,
      distribution_lifecycle_status,
      additional_data,
      confidential_transaction,
      category_value,
      category_name,
      refactor,
      labels,
      created_via,
    } = values;

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal`,
        {
          page_no,
          page_size: page_item_count,
          amount,
          min_amount,
          max_amount,
          lifecycle_status,
          payment_method,
          internal_account_id,
          owner_account_id,
          start_date,
          end_date,
          txn_id,
          entity_id,
          category,
          bank_identifier,
          distribution_lifecycle_status,
          additional_data,
          confidential_transaction:
            confidential_transaction === "Show only confidential"
              ? "Y"
              : confidential_transaction === "Hide confidential"
                ? "N"
                : undefined,
          category_name,
          category_value,
          refactor,
          labels,
          created_via,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        message:
          error?.response?.data?.errors?.base?.[
            GlobalConfiguration?.lengthCheckZero
          ] ||
          error?.response?.data?.base?.[GlobalConfiguration?.lengthCheckZero] ||
          error?.response?.data?.message ||
          error?.message,
        errors: error?.response?.data?.errors || {},
        data: error?.response?.data,
      });
    }
  },
);

export const internalTransactionsAddLabel = createAsyncThunk(
  "internalTransactions/internalTransactionsAddLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/add_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const internalTransactionsRemoveLabel = createAsyncThunk(
  "internalTransactions/internalTransactionsRemoveLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/remove_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const internalTransactionsDetailsById = createAsyncThunk(
  "internalTransactions/internalTransactionsDetailsById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/internal/${id}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    }
  },
);

export const internalTransactionsActionable = createAsyncThunk(
  "internalTransactions/internalTransactionsActionable",
  async (values, { rejectWithValue }) => {
    const { page_no, page_size, entityId, labels, txn_id, owner_account_id } =
      values;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/internal/actionable`,
        {
          headers: getHeaders(),
          params: {
            page_no: page_no ? page_no : undefined,
            page_size: page_size ? page_size : undefined,
            entity_id: entityId ? entityId : undefined,
            labels: labels,
            txn_id: txn_id,
            owner_account_id: owner_account_id,
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const internalTransactionsApprove = createAsyncThunk(
  "internalTransactions/internalTransactionsApprove",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/approve`,
        {
          txn_id: Array.isArray(id) ? id : [id],
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const internalTransactionsReject = createAsyncThunk(
  "internalTransactions/internalTransactionsReject",
  async ({ id, rejection_reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/reject`,
        {
          txn_id: Array.isArray(id) ? id : [id],
          reason: rejection_reason,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

const internalTransactionsSlice = createSlice({
  name: "internalTransactions",
  initialState: {
    storedPage: 1,
    toggle: false,
    internalTransactionAvailableLabels: [],

    internalTransactionsCreateData: null,
    internalTransactionsCreateLoading: false,
    internalTransactionsCreateError: null,

    internalTransactionsRecreateData: null,
    internalTransactionsRecreateLoading: false,
    internalTransactionsRecreateError: null,

    internalTransactionsViewAllData: null,
    internalTransactionsViewAllLoading: false,
    internalTransactionsViewAllError: null,

    internalTransactionsAddLabelData: null,
    internalTransactionsAddLabelLoading: false,
    internalTransactionsAddLabelError: null,

    internalTransactionsRemoveLabelData: null,
    internalTransactionsRemoveLabelLoading: false,
    internalTransactionsRemoveLabelError: null,

    internalTransactionsDetailsData: null,
    internalTransactionsDetailsLoading: false,
    internalTransactionsDetailsError: null,

    internalTransactionsActionableData: null,
    internalTransactionsActionableLoading: false,
    internalTransactionsActionableError: null,

    internalTransactionsApproveData: null,
    internalTransactionsApproveLoading: false,
    internalTransactionsApproveError: null,

    internalTransactionsRejectData: null,
    internalTransactionsRejectLoading: false,
    internalTransactionsRejectError: null,
  },
  reducers: {
    resetInternalTransactionsViewAll: (state) => {
      state.internalTransactionsViewAllData = null;
      state.internalTransactionsViewAllLoading = false;
      state.internalTransactionsViewAllError = null;
    },
    setToggle: (state, action) => {
      state.toggle = action.payload;
    },
    setAvailabelLabels: (state, action) => {
      state.internalTransactionAvailableLabels = action.payload;
    },
    resetToggle: (state) => {
      state.toggle = false;
    },
    updateInternalTxnDetailData: (state, action) => {
      state.internalTransactionsDetailsData = action.payload;
    },
    resetInternalTransactionReject: (state) => {
      state.internalTransactionsRejectError = false;
    },
    resetInternalTransactionApprove: (state) => {
      state.internalTransactionsApproveError = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(internalTransactionsCreate.pending, (state) => {
        state.internalTransactionsCreateLoading = true;
      })
      .addCase(internalTransactionsCreate.fulfilled, (state, action) => {
        state.internalTransactionsCreateLoading = false;
        state.internalTransactionsCreateData = action?.payload;
      })
      .addCase(internalTransactionsCreate.rejected, (state, action) => {
        if (action?.payload?.data?.errors) {
          sessionStorage.setItem(
            "internalTxnCreateErrors",
            JSON.stringify(action?.payload?.data?.errors),
          );
        } else {
          sessionStorage.setItem(
            "internalTxnCreateErrors",
            JSON.stringify(action?.payload?.data?.error),
          );
        }
        state.internalTransactionsCreateLoading = false;
        state.internalTransactionsCreateData = null;
        state.internalTransactionsCreateError = action?.payload;
      })
      .addCase(internalTransactionsRecreate.pending, (state) => {
        state.internalTransactionsRecreateLoading = true;
      })
      .addCase(internalTransactionsRecreate.fulfilled, (state, action) => {
        state.internalTransactionsRecreateLoading = false;
        state.internalTransactionsRecreateData = action?.payload;
      })
      .addCase(internalTransactionsRecreate.rejected, (state, action) => {
        state.internalTransactionsRecreateLoading = false;
        state.internalTransactionsRecreateData = null;
        state.internalTransactionsRecreateError = true;
      })
      .addCase(internalTransactionsViewAll.pending, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.internalTransactionAvailableLabels = [];
        }
        state.internalTransactionsViewAllLoading = true;
      })
      .addCase(internalTransactionsViewAll.fulfilled, (state, action) => {
        state.internalTransactionAvailableLabels =
          action?.payload?.summary?.available_labels;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "internalTxnLabels",
            JSON.stringify(action?.payload?.summary?.available_labels),
          );
        }
        state.internalTransactionsViewAllLoading = false;
        state.internalTransactionsViewAllData = action.payload;
        state.internalTransactionAvailableLabels =
          action?.payload?.summary?.available_labels;
        if (action?.payload?.summary?.available_labels) {
          sessionStorage.setItem(
            "internalTxnLabels",
            JSON.stringify(action?.payload?.summary?.available_labels),
          );
        }
      })
      // .addCase(internalTransactionsViewAll.fulfilled, (state, action) => {
      //   const availableLabels = action.payload?.summary?.available_labels ;
      //   state.internalTransactionsViewAllLoading = false;
      //   state.internalTransactionsViewAllData = action.payload;
      //   state.internalTransactionsViewAllError = null;
      //   state.internalTransactionAvailableLabels = availableLabels;

      //   sessionStorage.setItem(
      //     "internalTxnLabels",
      //     JSON.stringify(availableLabels),
      //   );
      // })
      .addCase(internalTransactionsViewAll.rejected, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.internalTransactionAvailableLabels = [];
        }
        state.internalTransactionsViewAllLoading = false;
        state.internalTransactionsViewAllData = null;
        state.internalTransactionsViewAllError =
          action.payload?.message || action.error?.message;
      })

      .addCase(internalTransactionsViewAllWithBody.pending, (state, action) => {
        if (action?.payload?.summary?.available_labels) {
          state.internalTransactionAvailableLabels = [];
        }
        state.internalTransactionsViewAllLoading = true;
      })
      .addCase(
        internalTransactionsViewAllWithBody.fulfilled,
        (state, action) => {
          state.internalTransactionAvailableLabels =
            action?.payload?.summary?.available_labels;
          if (action?.payload?.summary?.available_labels) {
            sessionStorage.setItem(
              "internalTxnLabels",
              JSON.stringify(action?.payload?.summary?.available_labels),
            );
          }
          state.internalTransactionsViewAllLoading = false;
          state.internalTransactionsViewAllData = action.payload;
          state.internalTransactionAvailableLabels =
            action?.payload?.summary?.available_labels;
          if (action?.payload?.summary?.available_labels) {
            sessionStorage.setItem(
              "internalTxnLabels",
              JSON.stringify(action?.payload?.summary?.available_labels),
            );
          }
        },
      )
      .addCase(
        internalTransactionsViewAllWithBody.rejected,
        (state, action) => {
          if (action?.payload?.summary?.available_labels) {
            state.internalTransactionAvailableLabels = [];
          }
          state.internalTransactionsViewAllLoading = false;
          state.internalTransactionsViewAllData = null;
          state.internalTransactionsViewAllError =
            action.payload?.message || action.error?.message;
        },
      )

      .addCase(internalTransactionsAddLabel.pending, (state) => {
        state.internalTransactionsAddLabelLoading = true;
        state.internalTransactionsAddLabelError = null;
      })
      .addCase(internalTransactionsAddLabel.fulfilled, (state, action) => {
        state.internalTransactionsAddLabelLoading = false;
        state.internalTransactionsAddLabelData = action.payload;
      })
      .addCase(internalTransactionsAddLabel.rejected, (state, action) => {
        state.internalTransactionsAddLabelLoading = false;
        state.internalTransactionsAddLabelData = null;
        state.internalTransactionsAddLabelError = action.payload || true;
      })
      .addCase(internalTransactionsRemoveLabel.pending, (state) => {
        state.internalTransactionsRemoveLabelLoading = true;
        state.internalTransactionsRemoveLabelError = null;
      })
      .addCase(internalTransactionsRemoveLabel.fulfilled, (state, action) => {
        state.internalTransactionsRemoveLabelLoading = false;
        state.internalTransactionsRemoveLabelData = action.payload;
      })
      .addCase(internalTransactionsRemoveLabel.rejected, (state, action) => {
        state.internalTransactionsRemoveLabelLoading = false;
        state.internalTransactionsRemoveLabelData = null;
        state.internalTransactionsRemoveLabelError = action.payload || true;
      })
      .addCase(internalTransactionsDetailsById.pending, (state, action) => {
        state.internalTransactionsDetailsLoading = true;
      })
      .addCase(internalTransactionsDetailsById.fulfilled, (state, action) => {
        state.internalTransactionsDetailsLoading = false;
        state.internalTransactionsDetailsData = action.payload;
      })
      .addCase(internalTransactionsDetailsById.rejected, (state, action) => {
        state.internalTransactionsDetailsError = action.payload || true;
      })
      .addCase(internalTransactionsActionable.pending, (state, action) => {
        state.internalTransactionsActionableLoading = true;
      })
      .addCase(internalTransactionsActionable.fulfilled, (state, action) => {
        state.internalTransactionsActionableLoading = false;
        state.internalTransactionsActionableData = action.payload;
      })
      .addCase(internalTransactionsActionable.rejected, (state, action) => {
        state.internalTransactionsActionableError = true;
        state.internalTransactionsActionableData = null;
      })
      .addCase(internalTransactionsApprove.pending, (state, action) => {
        state.internalTransactionsApproveLoading = true;
      })
      .addCase(internalTransactionsApprove.fulfilled, (state, action) => {
        state.internalTransactionsApproveLoading = false;
        state.internalTransactionsApproveData = action.payload;
      })
      .addCase(internalTransactionsApprove.rejected, (state, action) => {
        sessionStorage.setItem(
          "internalTxnError",
          JSON.stringify(action.payload?.errors?.base),
        );
        state.internalTransactionsApproveError = true;
        state.internalTransactionsApproveData = null;
      })
      .addCase(internalTransactionsReject.pending, (state, action) => {
        state.internalTransactionsRejectLoading = true;
      })
      .addCase(internalTransactionsReject.fulfilled, (state, action) => {
        state.internalTransactionsRejectLoading = false;
        state.internalTransactionsRejectData = action.payload;
      })
      .addCase(internalTransactionsReject.rejected, (state, action) => {
        state.internalTransactionsRejectError = true;
        state.internalTransactionsRejectData = null;
        state.internalTransactionsRejectLoading = false;
        sessionStorage.setItem(
          "internalTxnError",
          JSON.stringify(action.payload?.errors?.base),
        );
      });
  },
});

export const {
  setToggle,
  resetToggle,
  resetInternalTransactionsViewAll,
  updateInternalTxnDetailData,
  resetInternalTransactionReject,
  resetInternalTransactionApprove,
} = internalTransactionsSlice.actions;

export default internalTransactionsSlice.reducer;
